import { useState, useEffect, useRef } from 'react'
import { Quote, ChevronLeft, ChevronRight } from 'lucide-react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const testimonials = [
  {
    id: '01',
    text: 'TECHNIFIRE built our complete SaaS platform from scratch — from the initial architecture to launch in under 4 months. The multi-tenant system, subscription billing, and admin dashboard are rock-solid. Our platform now serves 2,000+ paying customers.',
    author: 'Marcus Chen',
    role: 'CEO & Co-Founder, DeepShield AI',
    service: 'SaaS Platform Development',
    metric: '2,000+ paying customers',
  },
  {
    id: '02',
    text: 'We needed an AI chatbot that could handle complex customer support queries across WhatsApp and our website. TECHNIFIRE delivered a solution that now automates 78% of our support tickets. Our team can finally focus on high-value conversations.',
    author: 'Sarah Williams',
    role: 'Head of Customer Experience, ValidSoft',
    service: 'AI Automation & Chatbots',
    metric: '78% support automation',
  },
  {
    id: '03',
    text: 'The e-commerce platform TECHNIFIRE built for us completely transformed our online business. The custom Shopify integration, AR product preview, and conversion-optimized checkout increased our sales by 340% in the first quarter.',
    author: 'Elena Rodriguez',
    role: 'Founder, MiroMark Interiors',
    service: 'E-Commerce Development',
    metric: '340% sales increase',
  },
  {
    id: '04',
    text: 'Our old CRM was a mess — clunky, slow, and did not match our workflow. TECHNIFIRE built a custom CRM that fits our real estate operations perfectly. Lead management, property matching, and client communication are now seamless.',
    author: 'James Wilson',
    role: 'Managing Director, Ownerz Properties',
    service: 'CRM System Development',
    metric: '60% faster deal closing',
  },
  {
    id: '05',
    text: 'The brand identity TECHNIFIRE created for us was beyond what we imagined. From the logo to the complete design system, every touchpoint feels cohesive and premium. Our investors specifically mentioned the quality of our brand at our Series A.',
    author: 'Amara Okafor',
    role: 'Co-Founder, BaseOne Fintech',
    service: 'Branding & Design',
    metric: 'Featured on Awwwards',
  },
]

export default function Testimonials() {
  const [activeIndex, setActiveIndex] = useState(0)
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      const elements = sectionRef.current?.querySelectorAll('.test-animate')
      if (elements && elements.length > 0) {
        gsap.fromTo(elements, { y: 50, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.8, stagger: 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 80%', toggleActions: 'play none none none' }
        })
      }
    }, sectionRef)
    return () => ctx.revert()
  }, [])

  const next = () => setActiveIndex((i) => (i + 1) % testimonials.length)
  const prev = () => setActiveIndex((i) => (i - 1 + testimonials.length) % testimonials.length)

  useEffect(() => {
    const id = setInterval(next, 2000)
    return () => clearInterval(id)
  }, [])

  const t = testimonials[activeIndex]

  return (
    <section ref={sectionRef} className="bg-[#050505] py-16 lg:py-24 px-6 lg:px-10">
      <div className="test-animate flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
        <div>
          <span className="text-mono text-[#9A9A9A] mb-2 block">TESTIMONIALS</span>
          <div className="flex items-baseline gap-4">
            <span className="text-6xl lg:text-8xl font-light text-white">150+</span>
            <span className="text-lg lg:text-xl text-white uppercase">HAPPY CLIENTS</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={prev} className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/5 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={next} className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/5 transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Ruler tabs */}
      <div className="test-animate relative mb-12">
        <div className="h-px bg-white/10 absolute top-1/2 left-0 right-0" />
        <div
          className="h-px bg-white absolute top-1/2 left-0 transition-all duration-700 ease-out"
          style={{ width: `${activeIndex * (100 / (testimonials.length - 1))}%` }}
        />
        <div className="flex justify-between relative">
          {testimonials.map((item, i) => (
            <button key={item.id} onClick={() => setActiveIndex(i)} className="flex flex-col items-center gap-2 transition-colors cursor-pointer">
              <span className={`text-mono ${activeIndex === i ? 'text-white' : 'text-[#666]'}`}>{item.id}</span>
              <div className={`w-2 h-2 rounded-full transition-all ${activeIndex === i ? 'bg-white scale-125' : 'bg-[#666]'}`} />
            </button>
          ))}
        </div>
      </div>

      {/* Testimonial content */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
        {/* Left: Service badge + metric */}
        <div className="lg:col-span-1 test-animate">
          <div className=" bg-gradient-to-r from-[#B7000E] via-[#b7000f94] to-[#b7000f5f]rounded-2xl p-6">
            <span className="text-mono text-[#9A9A9A] mb-3 block">SERVICE</span>
            <p key={t.service} className="text-white text-sm font-medium mb-4 animate-fade-in">{t.service}</p>
            <div className="h-px bg-white/10 mb-4" />
            <span className="text-mono text-[#9A9A9A] mb-1 block">RESULT</span>
            <p key={t.metric} className="text-2xl font-light text-white animate-fade-in">{t.metric}</p>
          </div>
        </div>

        {/* Right: Quote */}
        <div className="lg:col-span-4 test-animate">
          <Quote className="w-10 h-10 text-white/10 mb-4" />
          <p key={t.text} className="text-xl lg:text-2xl text-white leading-relaxed mb-8 animate-slide-up">"{t.text}"</p>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-white/20 to-white/5 flex items-center justify-center text-white font-medium">
              {t.author.split(' ').map(n => n[0]).join('')}
            </div>
            <div>
              <p key={t.author} className="text-white font-medium animate-fade-in">{t.author}</p>
              <p key={t.role} className="text-sm text-[#9A9A9A] animate-fade-in">{t.role}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
